<template>
  <div class="v2-select-wrapper">
    <label
      v-if="label"
      class="v2-select__label"
    >{{ label }}</label>
    <v-select
      class="v2-select"
      :class="{ 'v2-select--disabled': disabled, 'v2-select--error': error }"
      :value="value"
      :options="options"
      :placeholder="placeholder"
      :disabled="disabled"
      :multiple="multiple"
      :label="optionLabel"
      :reduce="reduce || undefined"
      :clearable="clearable"
      v-on="$listeners"
      @input="val => $emit('input', val)"
    />
    <small
      v-if="error"
      class="v2-select__error"
    >{{ error }}</small>
  </div>
</template>

<script>
import vSelect from 'vue-select'

export default {
  name: 'V2Select',
  components: { vSelect },
  props: {
    value: { type: [String, Number, Array, Object], default: null },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Select...' },
    disabled: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    optionLabel: { type: String, default: 'label' },
    reduce: { type: Function, default: null },
    clearable: { type: Boolean, default: true },
    error: { type: String, default: '' },
    label: { type: String, default: '' },
  },
}
</script>

<style lang="scss" scoped>
.v2-select-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--v2-space-1);
}

.v2-select__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--v2-foreground);
}

.v2-select__error {
  font-size: 0.75rem;
  color: var(--v2-destructive);
}

.v2-select--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>

<style lang="scss">
/* Unscoped to override vue-select internals */
/* Trigger — translucent fill + glass-stroke, NO `backdrop-filter`. The
 * trigger is one of many form controls on a page; per-trigger blur is
 * GPU-expensive and the blur effect is not visually useful at this size.
 * The dropdown menu (.vs__dropdown-menu below) keeps its blur because
 * only one menu is open at a time. */
.v2-select .vs__dropdown-toggle {
  min-height: 38px;
  padding: 0 var(--v2-space-2);
  border: 1px solid var(--v2-glass-stroke);
  border-radius: var(--v2-radius-md);
  background: var(--v2-glass-surface-2);
  font-family: var(--v2-font-sans);
  font-size: 0.875rem;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.v2-select:not(.vs--open) .vs__dropdown-toggle:hover {
  border-color: var(--v2-glass-stroke-strong);
}

.v2-select .vs__search::placeholder {
  color: var(--v2-muted-foreground);
}

.v2-select.vs--open .vs__dropdown-toggle {
  border-color: var(--v2-primary);
  box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
}

/* Floating surface — near-opaque white so dark text stays readable when
 * the menu opens over arbitrary content (product images, dark cards). */
.v2-select .vs__dropdown-menu {
  background: var(--v2-glass-surface-floating);
  backdrop-filter: blur(var(--v2-glass-blur-sm)) saturate(160%);
  border: 1px solid var(--v2-glass-stroke);
  border-radius: 1rem;
  box-shadow: var(--v2-glass-shadow-floating);
  font-family: var(--v2-font-sans);
  font-size: 0.875rem;
}

.v2-select .vs__dropdown-option--highlight {
  background: var(--v2-glass-surface-1);
  color: var(--v2-foreground);
}

.v2-select .vs__selected {
  color: var(--v2-foreground);
  background: var(--v2-glass-surface-2);
  border: 1px solid var(--v2-glass-stroke);
  border-radius: var(--v2-radius-sm);
  padding: 2px 6px;
  margin: 2px;
  font-size: 0.875rem;
}

.v2-select .vs__deselect {
  fill: var(--v2-muted-foreground);
  margin-left: 4px;
}

.v2-select .vs__clear,
.v2-select .vs__open-indicator {
  fill: var(--v2-muted-foreground);
}

.v2-select--error .vs__dropdown-toggle {
  border-color: var(--v2-destructive);
}

@media (prefers-reduced-transparency: reduce) {
  .v2-select .vs__dropdown-toggle {
    background: var(--v2-card);
  }
  .v2-select .vs__dropdown-menu {
    background: var(--v2-card);
    backdrop-filter: none;
    box-shadow: var(--v2-shadow-md);
  }
}
</style>
